/**
 * The company a deal already names but does not link to.
 *
 * Hanneza, 10 September: "Spireworks is completely missing from the dropdown
 * list, preventing me from tagging the deal properly. I am also running into
 * this same issue with a few other deals."
 *
 * She was right that it was missing and wrong about why. Nothing was hidden
 * or truncated: companies RLS gives every sales member every row, and the
 * query has no limit. The dropdown lists company RECORDS, and Spireworks was
 * never one. It exists as typed text on Jeanne's lead, in the free-text
 * company field a lead carries alongside its optional company_id.
 *
 * A read of production found 28 names in that state, behind 33 leads. The
 * chain stops one link short every time: the deal knows its person, the
 * person names their company in words, and no record was ever made.
 *
 * ── Why the name comes from the lead rather than a text box ────────────────
 *
 * All 28 came in this way. The name is already sitting on the contact, spelt
 * the way whoever entered the lead spelt it, so offering THAT is both fewer
 * keystrokes and less room to invent a second spelling. "Roland Corporation"
 * created beside an existing "Roland" is the failure this avoids, and it is
 * the one the earlier link scripts warned about in writing.
 *
 * ── What it deliberately will not do ───────────────────────────────────────
 *
 * It offers nothing when a record already exists under that name, however it
 * is capitalised. CELSIUS is in the system and a lead types Celsius; that is
 * a LINK, not a create, and a second Celsius record is exactly the mess this
 * is meant to prevent. The picker already lists it, so the person can select
 * it in one click and no button is needed.
 */

export interface NamedCompany {
  label: string;
}

/**
 * The name to offer creating, or null when there is nothing to offer.
 *
 * Null covers every case where creating would be wrong or pointless: the deal
 * already has a company, the contact named none, or a record already exists
 * under that name in some other casing.
 */
export function companyToCreate(input: {
  /** The deal's current company link. */
  companyId: string | null;
  /** What the deal's contact typed for their company. */
  leadCompanyName: string | null | undefined;
  /** Every company record currently loaded. */
  companies: NamedCompany[];
}): string | null {
  if (input.companyId !== null) {
    return null;
  }
  const typed = (input.leadCompanyName ?? '').trim();
  if (typed === '') {
    return null;
  }
  // Case-insensitive, because CELSIUS and Celsius are one company and the
  // dropdown is already offering the record under whichever spelling it has.
  const key = typed.toLowerCase();
  if (input.companies.some((company) => company.label.trim().toLowerCase() === key)) {
    return null;
  }
  return typed;
}
